import * as React from 'react';
import MyTypes from 'MyTypes';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, ConnectedProps } from 'react-redux';
import { Card, Form, Button, Row, Col } from 'react-bootstrap';
import SingleActionButton from '../common/SingleActionButton';
import addIcon from '../../assets/images/Blue/add.svg';
import * as router from '../../services/router-service'; 
import * as models from '../../models/';
import { companyActions } from 'store/companies';
import CompanyTable from './CompanyList';
import Pager from 'components/common/Pager';
import '../../assets/styles/Administration.css'; 
import { contactsActions } from 'store/contacts';

interface Props extends PropsFromRedux {}

interface State {
    companyListRequest: models.CompaniesRequest; 
}

class CompanySearch extends React.Component<Props, State> {
    public state = {
        companyListRequest: {
            ...models.defaultCompanyRequest,
            ...models.defaultPaging
        }, 
    };

    public componentDidMount(): void {
        this.fetchFormData({
            ...this.state.companyListRequest,
        });
    }

    private handleSearchSubmit = async (e: any) => {
        e.preventDefault(); 
        this.fetchFormData(this.state.companyListRequest); 
    };

    public fetchFormData = (request: models.CompaniesRequest) => {     
        this.props.requestCompanyList(request);
        this.props.requestFieldDirectorsDD();
    }

    public addNew = () => {        
        router.goto(router.url.companyDetail.replace(":companyId", '0')); 
    }

    public resetFilters = () => {
        const resetRequest = {
            ...models.defaultCompanyRequest,
            ...models.defaultPaging
        };
        this.setState({ companyListRequest: resetRequest }); 
        this.fetchFormData(resetRequest);
    }

    public pagerCallback = (paging: models.Paging) => { 
        const sizeChanged = paging.pageSize !== this.state.companyListRequest.pageSize;
        const request: models.CompaniesRequest = {
            ...this.state.companyListRequest,
            ...paging,   
            pageNo: sizeChanged ? 1 : paging.pageNo
        };
        this.setState({companyListRequest: request}); 
        this.props.requestCompanyList(request); 
    };

    // Helper to handle dropdown changes cleanly
    private handleDropdownChange = (field: keyof models.CompaniesRequest, value: any) => {
        const newRequest = {
            ...this.state.companyListRequest,
            [field]: value,
            pageNo: 1 // Reset to page 1 on filter change
        };
        
        this.setState({ companyListRequest: newRequest });
        this.fetchFormData(newRequest);
    }

    render() {
        const { companyListRequest } = this.state;
        const { companyListResponse, fieldDirectorList } = this.props;
        const showPagination = companyListResponse?.totalCount > companyListResponse?.pageSize! ? true : false; 

        return (
            <>
                <Card className="claim-card">
                    <Card.Title>Company Search
                        <SingleActionButton
                            action="Add"
                            title="Company"
                            img={addIcon}
                            onClick={this.addNew}
                        />
                    </Card.Title>
                    <Form onSubmit={this.handleSearchSubmit}>
                        <Card.Body style={{display: "flex", flexDirection: "column", gap: "15px"}}>
                            <Row>                                
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Company Name</Form.Label>
                                        <Form.Control 
                                            name="name" 
                                            placeholder='Company Name...' 
                                            value={companyListRequest.name || ""} 
                                            onChange={(e) => this.setState({companyListRequest: {...companyListRequest, name: e.target.value}})} 
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Type</Form.Label>
                                        <Form.Select 
                                            name="type" 
                                            value={companyListRequest.type || ""} 
                                            onChange={(e) => this.handleDropdownChange('type', e.target.value)} 
                                        >
                                            <option value="">All Types</option>
                                            <option value="Adjuster">Adjuster</option>
                                            <option value="Agent">Agent</option>
                                            <option value="Independent Adjuster">Independent Adjuster</option>
                                            <option value="Insurance">Insurance</option>
                                            <option value="Mortgage">Mortgage</option>
                                        </Form.Select>                                    
                                    </Form.Group>    
                                </Col>    
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select 
                                            name="activeStatus"
                                            value={companyListRequest.activeStatus || "all"}
                                            onChange={(e) => this.handleDropdownChange('activeStatus', e.target.value)}
                                        >
                                            <option value="all">All</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>  
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Has Active Claims</Form.Label>
                                        <Form.Select 
                                            name="hasActiveClaims"
                                            value={companyListRequest.hasActiveClaims?.toString() || ""}
                                            onChange={(e) => this.handleDropdownChange('hasActiveClaims', e.target.value)}
                                        >
                                            <option value="">All</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Has Lifetime Claims</Form.Label>
                                        <Form.Select 
                                            name="hasLifetimeClaims"
                                            value={companyListRequest.hasLifetimeClaims?.toString() || ""}
                                            onChange={(e) => this.handleDropdownChange('hasLifetimeClaims', e.target.value)}
                                        >
                                            <option value="">All</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Last Claim (Start)</Form.Label>
                                        <Form.Control 
                                            type="date"
                                            name="lastClaimDateStart"
                                            value={companyListRequest.lastClaimDateStart || ""}
                                            onChange={(e) => this.setState({
                                                companyListRequest: { ...companyListRequest, lastClaimDateStart: e.target.value }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Last Claim (End)</Form.Label>
                                        <Form.Control 
                                            type="date"
                                            name="lastClaimDateEnd"
                                            value={companyListRequest.lastClaimDateEnd || ""}
                                            onChange={(e) => this.setState({
                                                companyListRequest: { ...companyListRequest, lastClaimDateEnd: e.target.value }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Assigned Rep</Form.Label>
                                        <Form.Select 
                                            name="assignedRepId"
                                            value={companyListRequest.assignedRepId || ""}
                                            onChange={(e) => this.handleDropdownChange('assignedRepId', e.target.value)}
                                        >
                                            <option value="">All</option>
                                            <option value="-1">Unassigned</option>
                                            
                                            {fieldDirectorList?.resourceList?.map((rep: models.DropDownListItem, i: number) => (
                                                <option key={i} value={rep.id}>
                                                    {rep.name}
                                                </option>
                                            ))}
                                            
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>                           
                            {/* --- BUTTONS ROW --- */}
                            <Row>
                                <Col md={12} className="d-flex justify-content-end gap-2 mt-2">
                                    <Button className="claim-search-btn" type='submit'>Search</Button>
                                    <Button className="claim-search-btn" onClick={this.resetFilters}>Reset Filters</Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Form>
                </Card>
                
                <CompanyTable/>

                {showPagination &&
                <div>
                    <Pager
                        className = {"admin-pager"}
                        retrieveDataCallback = {this.pagerCallback}
                        pageSize = {companyListResponse?.pageSize || 20}
                        currentPage = {companyListResponse?.pageNo || 1}
                        totalCount = {companyListResponse?.totalCount || 0}
                        pagesInRange = {3}
                    />
                </div>
            }
            </>
        );
    }
}
const mapStateToProps = (state: MyTypes.RootState) => ({
    companyListResponse: state.companies.company.companiesResponse,
    fieldDirectorList: state.contacts.dropdowns.fieldDirectorsDD,
}); 

const mapDispatchToProps = (dispatch: Dispatch) => 
    bindActionCreators(
        {       
            requestCompanyList: companyActions.requestCompanyList, 
            requestFieldDirectorsDD: contactsActions.requestFieldDirectorsDD,
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(CompanySearch);